import React, { useEffect, useRef, useState } from 'react';
import { LevelData } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { motion } from 'framer-motion';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';
import NumberLine from '../components/NumberLine';

interface Step1IntroductionProps {
  level: LevelData;
  onComplete: () => void;
}

const Step1Introduction: React.FC<Step1IntroductionProps> = ({ level, onComplete }) => {
  const { playText, isLoading, isPlaying, error: ttsError, stop } = useTTS();
  const hasStarted = useRef(false);
  const [showExample, setShowExample] = useState(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    let cancelled = false;

    const playAndAdvance = async () => {
      // Phase 1: Play the rule audio
      await playText(level.ruleAudioText);
      if (cancelled) return;

      // Phase 2: If there's an example, show the visualization and play example audio
      if (level.ruleExampleAudioText) {
        setShowExample(true);
        await playText(level.ruleExampleAudioText);
        if (cancelled) return;
      }

      // Small delay after audio finishes before advancing
      setTimeout(() => {
        if (!cancelled) onComplete();
      }, 1000);
    };

    playAndAdvance();

    return () => {
      cancelled = true;
      stop();
      hasStarted.current = false;
    };
  }, [level.ruleAudioText, level.ruleExampleAudioText, playText, onComplete, stop]);

  const rangeStart = Math.min(...level.primaryFacts.map((f) => f.addend1));
  const rangeEnd = Math.max(...level.primaryFacts.map((f) => f.sum)) + 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-10 rounded-2xl shadow-lg text-center max-w-lg w-full"
    >
      <div className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">
        Level {level.level}: {level.name}
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">The Rule</h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-xl text-gray-700 leading-relaxed mb-8"
      >
        {level.rule}
      </motion.p>
      {level.visualization === 'number-line' && showExample && (
        <NumberLine
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          fact={level.primaryFacts[0]}
          animate={true}
          loop={true}
        />
      )}
      <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
    </motion.div>
  );
};

export default Step1Introduction;
